import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import RegisterLink from '@/components/ui/RegisterLink';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { applyPageSeo, BLOG_PAGE_SEO, HOME_PAGE_SEO } from '@/lib/seo';
import Bybid9CasinoAustraliaArticle from './components/Bybid9CasinoAustraliaArticle';

const DOC_TITLE = BLOG_PAGE_SEO.title;
const DOC_DESC = BLOG_PAGE_SEO.description;

const TOC_LINKS: { id: string; label: string }[] = [
  { id: 'australian-market', label: 'Australian market' },
  { id: 'trusted-pokies-australia', label: 'TPA (Trusted Pokies Australia)' },
  { id: 'football-bet', label: 'Football bet' },
  { id: 'providers-ecosystem', label: 'Provider ecosystem' },
  { id: 'provider-jili', label: 'JILI' },
  { id: 'provider-bng', label: 'BNG' },
  { id: 'provider-pragmatic', label: 'Pragmatic Play' },
  { id: 'provider-vpower', label: 'VPower' },
  { id: 'provider-mega888', label: 'MEGA888' },
  { id: 'provider-bt', label: 'BT Gaming' },
  { id: 'depositchecklist', label: 'Checklist' },
  { id: 'faq', label: 'FAQ' },
  { id: 'conclusion', label: 'Conclusion' },
];

export default function BlogPage() {
  useEffect(() => {
    applyPageSeo(BLOG_PAGE_SEO);
    return () => applyPageSeo(HOME_PAGE_SEO);
  }, []);

  return (
    <div className="min-h-screen site-bg font-sans overflow-x-hidden">
      <Navbar />
      <main className="pt-16 sm:pt-[4.5rem]">
        <section
          className="relative pt-6 sm:pt-8 pb-8 sm:pb-10 px-4 sm:px-5 md:px-8 overflow-hidden"
          style={{ background: 'linear-gradient(180deg, #071520 0%, #0d1117 50%, #091520 100%)' }}
        >
          <div className="section-divider absolute top-0 left-0 w-full" />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: 'radial-gradient(ellipse at 30% 0%, rgba(71,202,241,0.08) 0%, transparent 50%)' }}
          />
          <div className="relative max-w-6xl mx-auto">
            <nav className="text-xs text-[#56626B] mb-6" aria-label="Breadcrumb">
              <Link to="/" className="text-[#47CAF1] hover:underline">
                Home
              </Link>
              <span className="mx-2 text-white/30">/</span>
              <span className="text-white/60">Blog</span>
            </nav>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div>
                <div className="badge-blue mb-4 w-fit">
                  <i className="ri-article-line" aria-hidden />
                  Bybid9 Blog
                </div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-black text-white leading-tight mb-2">
                  Industry notes &amp; long reads
                </h2>
                <p className="text-[#56626B] text-sm md:text-base max-w-xl leading-relaxed">
                  Evidence-led breakdowns of the online casino industry, Australian player behaviour, and major game studios.
                  Updated when we rewrite sections.
                </p>
              </div>
              <RegisterLink
                section="blog_hero"
                linkLabel="Live promotions"
                href="https://bybid9.com/promotion"
                target="_blank"
                rel="noreferrer"
                className="btn-premium text-sm px-6 py-3.5 min-h-[48px] w-full sm:w-auto inline-flex items-center justify-center shrink-0 self-stretch sm:self-start lg:self-auto"
              >
                <span>Live promotions</span>
              </RegisterLink>
            </div>
          </div>
        </section>

        <section className="relative py-8 sm:py-12 md:py-16 px-4 sm:px-5 md:px-8 pb-16 sm:pb-24" style={{ background: '#0d1117' }}>
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[minmax(0,240px)_minmax(0,1fr)] gap-8 sm:gap-10 lg:gap-14">
            <aside className="lg:sticky lg:top-28 h-fit order-2 lg:order-1">
              <nav
                className="glass-card rounded-2xl p-5"
                style={{ border: '1px solid rgba(71,202,241,0.12)' }}
                aria-label="On this page"
              >
                <p className="text-white font-bold text-xs uppercase tracking-wider mb-4 flex items-center gap-2">
                  <i className="ri-list-unordered text-[#47CAF1]" aria-hidden />
                  On this page
                </p>
                <ul className="space-y-1 text-xs">
                  {TOC_LINKS.map((item) => (
                    <li key={item.id}>
                      <a
                        href={`#${item.id}`}
                        className="text-[#56626B] hover:text-[#47CAF1] active:text-[#47CAF1] transition-colors leading-snug block py-1.5 sm:py-0.5 border-l-2 border-transparent hover:border-[#47CAF1]/40 pl-2 -ml-0.5"
                      >
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </aside>

            <article
              className="glass-card rounded-2xl sm:rounded-3xl p-5 sm:p-8 md:p-10 lg:p-12 order-1 lg:order-2 min-w-0"
              style={{ border: '1px solid rgba(71,202,241,0.12)' }}
              itemScope
              itemType="https://schema.org/Article"
            >
              <meta itemProp="headline" content={DOC_TITLE} />
              <meta itemProp="description" content={DOC_DESC} />
              <Bybid9CasinoAustraliaArticle />
            </article>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
